import React, { Fragment } from 'react';
import Header from '@components/Header';
import { graphql, Link } from 'gatsby';
import Footer from '@components/Footer';
import Wistia from '@components/Wistia';
import Gallery from '@components/Gallery';
import { css } from '@emotion/react';
import { map, get } from 'lodash';
import BackgroundImage from 'gatsby-background-image';
import styled from '@emotion/styled';

const sections = [{
  name: 'name',
  foundingDate: 'founding_date',
  website: 'website',
  email: 'email',
  address: 'address'
},
{
  summary: 'summary',
  description: 'description'
}];

const headers = ['info', 'video', 'images', 'logos', 'testimonials', 'founders', 'partners', 'awards', 'references', 'contact'];

const Card = styled.div`
  background: #F8F9FA;
  display: flex;
  flex-direction: column;
  .spacer {
    width: 100%;
    padding-bottom: 56%;
  }
  .body {
    padding: 1rem;
    flex-grow: 2;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .info {
      display: flex;
      align-items: center;

      justify-content: space-between;
      font-size: 13px;
      .author {
        color: var(--primary);
      }
    }
  }
`;

const renderSections = (data, values) => {
  return map(values,
    (value, i) =>
      <Fragment key={i}>
        <h5>
          {
            value === 'email' ?
              get(data, 'info_company_contact_email_label') :
              get(data, `info_company_info_section_${value}_label`)
          }
        </h5>
        <p>
          {value === 'email' ?
            get(data, 'info_company_info_section_contact_email_value') :
            get(data, `info_company_info_section_${value}_value`)
          }
        </p>
      </Fragment>
  );
};

const renderGallery = (data, header) => {
  let images = get(data, `info_company_${header}_section_images`);
  return (
    <div key={header}>
      <Gallery
        data={images}
        imagesPerRow={header == 'images' ? 3 : 2}
      />
    </div>
  );
};

// eslint-disable-next-line complexity
const renderElements = (data, header) => {
  switch (header) {
    case 'info': return (
      <div className='row'>
        <div className='col-sm-3'>
          {renderSections(data, sections[0])}
        </div>
        <div className='col-sm-9'>
          {renderSections(data, sections[1])}
        </div>
      </div>
    );
    case 'video': return (<Wistia wistiaId={data.info_company_video_section_wistia_id} />);
    case 'images': return (renderGallery(data, header));
    case 'logos': return (renderGallery(data, header));
    case 'testimonials': return (
      data.info_company_testimonials_section_entries.map(
        ({ text, author_name, author_profession }, i) =>
          <div key={i} className="mb-3">
            <blockquote className="blockquote">
              <p className="mb-0">{text}</p>
              <footer className="blockquote-footer">
                {author_name}, <cite>{author_profession}</cite>
              </footer>
            </blockquote>
          </div>
      )
    );
    case 'partners':
    case 'awards':
    case 'references': return (renderCards(data, header));
    case 'founders': return (data.info_company_founders_section_entries.map(
      ({ description, name }, i) => (
        <div key={i}>
          <h5>{name}</h5>
          <p>{description}</p>
        </div>)));
    case 'contact': return (
      <div className='row'>
        <div className='col-sm-6'>
          {renderSections(data, ['email'])}
        </div>
        <div className='col-sm-6'>
          {renderSections(data, ['address'])}
        </div>
      </div>
    );
  }
};

const renderer = (data, header) => {
  return (
    <div
      id={header}
      key={header}
      className='Press-section mb-5'
    >
      <h2
        className='text-center mb-3'
        css={css`
        font-family: 'Lato', sans-serif;
        margin-bottom: 50px !important;
        `}
      >
        {get(data, `info_company_${header}_section_header`)}
      </h2>
      <div className='Press-section-body'>{
        renderElements(data, header)
      }
      </div>
    </div>
  );
};

const renderCards = (data, header) => {
  // console.log(data, header);
  return (
    <div
      className="container"
    >
      <div
        className="row align-items-stretch"
      >
        {data[`info_company_${header}_section_entries`].map((entry, i) =>
          <div
            key={i}
            css={css`
      display: flex;
      align-items: stretch;
      flex-direction: row
    `}
            className="col-12 col-md-6 col-lg-4"
          >
            <a
              key={i}
              href={entry.url}
            >
              <Card>
                <div
                  css={css`
                  background-size: contain;
                  height: 250px;
                  width: 250px;
                  `}
                >
                  {' '}
                  <BackgroundImage
                    fluid={entry.image.localFile.sharp.fluid}
                    css={css`
                  background-size: contain;
                  height: 100%;
                  width: 100%;
                  display: block;
                  margin-left: auto;
                  margin-right: auto;
                  `}
                  >
                    <div className="spacer" />
                  </BackgroundImage>
                </div>
                <div className="body">
                  <div>
                    {' '}
                    {
                      header == 'references' ?
                        <div>
                          <p
                            className="title h4 text-dark"
                            css={css`
                  padding: 20px 20px 5px;
                  `}
                          >{entry.name}
                          </p>
                          <p
                            className="excerpt text-dark"
                            css={css`
                  padding: 0px 5px 0px 20px;
                  `}
                          >{entry.title}
                          </p>
                        </div>
                        :
                        <h2
                          className="title text-dark"
                          css={css`
                  padding: 20px;
                  font-size: 1.3rem;
                  `}
                        >{entry.name}
                        </h2>
                    }
                  </div>
                </div>
              </Card>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default function PressTemplate({
  data: {
    page: { press: data }
  },
  pageContext: { language, refs, layouts }
}) {
  return (
    <Fragment>
      <div>
        <Header
          dark="true"
          language={language}
          refs={refs}
          layouts={layouts}
        />
      </div>
      <div className="container">
        <div className="row align-items-stretch">
          <div className='col-sm-3'>
            {
              headers.map(header =>
                <div key={header}>
                  <Link
                    to={
                      language.path ? /* FIXME: */
                        `${language.path}/media/#${header}` :
                        `${language.path}/press/#${header}`
                    }
                    css={css`
                    color: #282C30;
                    :hover {
                      color: #282C30;
                    }
                  `}
                  >
                    {get(data, `info_company_${header}_section_header`)}
                  </Link>
                </div>
              )
            }
          </div>
          <div className='col-sm-9'>
            {headers.map(header => renderer(data, header))}
          </div>
        </div>
      </div>
      <Footer
        language={language}
        layouts={layouts}
      />
    </Fragment>
  );
}

export const query = graphql`
  query($pressId: Int) {
    page: wordpressWpCustompages(wordpress_id: { eq: $pressId }) {
      id
      acf {
        language
      }
      press: childWordPressAcfPressSection {
        info_company_founders_section_entries {
          description
          name
        }
        info_company_images_section_images {
          image {
            localFile {
              sharp: childImageSharp {
                fixed {
                  ...GatsbyImageSharpFixed
                },
                fluid(maxWidth: 700) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
        info_company_contact_email_label
        info_company_contact_section_address_label
        info_company_contact_section_address_value
        info_company_contact_section_email_label
        info_company_contact_section_email_value
        info_company_contact_section_header
        info_company_founders_section_header
        info_company_images_section_header
        info_company_info_section_address_label
        info_company_info_section_address_value
        info_company_info_section_contact_email_value
        info_company_info_section_description_label
        info_company_info_section_description_value
        info_company_info_section_founding_date_label
        info_company_info_section_founding_date_value
        info_company_info_section_header
        info_company_info_section_name_label
        info_company_info_section_summary_label
        info_company_info_section_name_value
        info_company_info_section_summary_value
        info_company_info_section_website_label
        info_company_info_section_website_value
        info_company_partners_section_header
        info_company_partners_section_entries {
          name
          url
          image {
            localFile {
              sharp: childImageSharp {
                fixed {
                  ...GatsbyImageSharpFixed
                },
                fluid(maxWidth: 700) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
        info_company_logos_section_header
        info_company_logos_section_images {
          image {
            localFile {
              sharp: childImageSharp {
                fixed {
                  ...GatsbyImageSharpFixed
                },
                fluid(maxWidth: 700) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
        info_company_awards_section_entries {
          name
          url
          image {
            localFile {
              sharp: childImageSharp {
                fixed {
                  ...GatsbyImageSharpFixed
                },
                fluid(maxWidth: 700) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
        }
        info_company_awards_section_header
        info_company_references_section_entries {
          image {
            localFile {
              sharp: childImageSharp {
                fixed {
                  ...GatsbyImageSharpFixed
                },
                fluid(maxWidth: 700) {
                  ...GatsbyImageSharpFluid
                }
              }
            }
          }
          name
          title
          url
        }
        info_company_references_section_header
        info_company_testimonials_section_entries {
          author_name
          author_profession
          text
        }
        info_company_testimonials_section_header
        info_company_video_section_header
        info_company_video_section_wistia_id
      }
    }
  }
`;